import { v4 as uuidv4 } from 'uuid';
import type { History } from 'history';
import { WorkOrderTaskType } from '@xbcb/work-order-types';
import { AccountType, RecordType } from '@xbcb/shared-types';
import { KebabMenuItemKey, ModalKey, WorkOrderTab } from '@xbcb/ui-types';
import type { UsDrawbackClaim as UsDrawbackClaimRecord } from '@xbcb/api-gateway-client';
import { setModal } from 'actions/modals';
import { UsDrawbackClaim } from '../../../loadableComponents';
import { executeMutation } from 'libs/executeMutation';
import { getFrontendUsDrawbackClaimLargePayloadClient } from 'libs/uiUsDrawbackClaimLargePayloadClient';
import { WorkOrderRoute } from '..';
import { appRecordAbiDataMenuItem } from '../../../appRecordRoutes/utils/appRecordAbiDataMenuItem';
import { appRecordMemoMenuItem } from '../../../appRecordRoutes/utils/appRecordMemoMenuItem';
import {
  inquireWorkOrderItem,
  unblockWorkOrderItem,
} from '../../../appRecordRoutes/utils/inquireWorkOrderItem';
import { checkKebabMenuItemForWorkOrderTask } from '../../../appRecordRoutes/utils/checkKebabMenuItemForWorkOrderTask';
import {
  isClaimLocked,
  wasClaimFiledWithCbp,
} from 'components/UsDrawbackClaim/claimLock';
import { AppRecordKebabMenuItem } from '../../../types';
import {
  queryUsDrawbackClaim,
  updateUsDrawbackClaim,
  createUsDrawbackClaimCbpMutation,
  updateUsDrawbackClaimCbpMutation,
} from './usDrawbackClaim.query';
import { validateBeforeSubmit } from './validateBeforeSubmit';
import { appRecordSendAbiMessage } from '../../../appRecordRoutes/utils/appRecordSendAbiMessage';
import { findWorkOrderTaskAndModifyStepCompletion } from '../../../appRecordRoutes/utils/findWorkOrderTaskAndModifyStepCompletion';
import { findWorkOrderTaskByIdOrType } from 'libs/findMatchingWorkOrderTask';
import { getRecordPath } from 'libs/getRecordPath';

const hasClaimData = (
  record: Partial<UsDrawbackClaimRecord> | undefined,
): boolean =>
  Array.isArray(record?.matchPairs) ||
  Boolean(record?.drawbackClaimReference?.referenceKey);

const hasInlineMatchPairs = (
  record: Partial<UsDrawbackClaimRecord> | undefined,
): boolean => Array.isArray(record?.matchPairs);

const hasEntryNumber = (
  record: Partial<UsDrawbackClaimRecord> | undefined,
): boolean => Boolean(record?.entryNumber);

const canSubmitClaim = (
  record: Partial<UsDrawbackClaimRecord> | undefined,
): boolean => hasClaimData(record) && !isClaimLocked(record);

const submitClaimToCbp = async (
  updatedRecord: UsDrawbackClaimRecord,
): Promise<boolean> => {
  let isSubmittedToCbp = false;
  await executeMutation({
    mutation: wasClaimFiledWithCbp(updatedRecord)
      ? updateUsDrawbackClaimCbpMutation
      : createUsDrawbackClaimCbpMutation,
    variables: {
      idempotencyKey: uuidv4(),
      usDrawbackClaimId: updatedRecord.id,
    },
    successMessage: 'Claim submitted to CBP',
    onSuccess: () => {
      isSubmittedToCbp = true;
      return Promise.resolve();
    },
  });
  return isSubmittedToCbp;
};

const findExceptionTask = (
  updatedRecord: UsDrawbackClaimRecord,
  currentTaskParams: { taskId?: string; taskType?: string },
) => {
  const currentTask = findWorkOrderTaskByIdOrType(
    updatedRecord,
    currentTaskParams,
  );
  const isExceptionTask =
    currentTask?.definition.workOrderTaskType ===
    WorkOrderTaskType.US_DRAWBACK_CLAIM_EXCEPTION;
  return isExceptionTask ? currentTask : undefined;
};

const completeExceptionTaskAndOpenWorkOrder = async ({
  updatedRecord,
  exceptionTaskId,
  history,
}: {
  updatedRecord: UsDrawbackClaimRecord;
  exceptionTaskId: string;
  history: History;
}): Promise<void> => {
  await findWorkOrderTaskAndModifyStepCompletion({
    workOrder: updatedRecord,
    workOrderTaskType: WorkOrderTaskType.US_DRAWBACK_CLAIM_EXCEPTION,
    taskId: exceptionTaskId,
  });
  history.push({
    pathname: getRecordPath({
      recordType: RecordType.US_DRAWBACK_CLAIM,
      id: updatedRecord.id,
    }),
    search: `tab=${WorkOrderTab.Tasks}`,
  });
};

const usDrawbackClaimRoute: WorkOrderRoute = {
  Page: UsDrawbackClaim,
  recordType: RecordType.US_DRAWBACK_CLAIM,
  queries: { get: queryUsDrawbackClaim, update: updateUsDrawbackClaim },
  getTitleField: () => 'entryNumber',
  defaultEditMode: false,
  draftEditMode: ({ existingRecord }) => canSubmitClaim(existingRecord),
  transformUpdateRecordInput: async ({ input, existingRecord }) => {
    const READ_ONLY_FIELDS = [
      'status',
      'deadline',
      'operator',
      'group',
      'tasks',
      'milestones',
      'confirmations',
      'documents',
      'created',
      'updated',
      'deleted',
      'canceled',
      'cbpStatus',
      'cbpRejectionReasons',
      'inquiries',
      'pendingUpdates',
      'stateMachine',
      'drawbackClaimReference',
      'entryNumber',
      'brokerNumber',
    ] as const;

    const updateInput = { ...input };
    READ_ONLY_FIELDS.forEach((field) => {
      delete updateInput[field];
    });

    if (
      hasClaimData(existingRecord) &&
      !hasInlineMatchPairs(existingRecord) &&
      !updateInput.matchPairs?.length
    ) {
      delete updateInput.matchPairs;
    }

    if (updateInput.matchPairs?.length) {
      const largePayloadClient = getFrontendUsDrawbackClaimLargePayloadClient();
      return largePayloadClient.processForOversizedInput(updateInput);
    }
    return updateInput;
  },
  kebabMenuItems: [
    appRecordAbiDataMenuItem,
    appRecordSendAbiMessage,
    inquireWorkOrderItem,
    unblockWorkOrderItem,
    appRecordMemoMenuItem,
    {
      text: 'Send Documents to CBP',
      key: KebabMenuItemKey.SEND_DOCUMENTS_TO_CBP,
      action: ({ dispatch }) => {
        dispatch(
          setModal({
            key: ModalKey.SEND_DOCUMENT,
            props: { visible: true, document: null },
          }),
        );
      },
      show: ({
        currentUser,
        existingRecord,
        workOrderTaskType,
        workOrderTaskEvaluatedState,
      }) =>
        currentUser.accountType === AccountType.OPERATOR &&
        hasClaimData(existingRecord) &&
        hasEntryNumber(existingRecord) &&
        checkKebabMenuItemForWorkOrderTask(
          workOrderTaskType,
          workOrderTaskEvaluatedState,
          KebabMenuItemKey.SEND_DOCUMENTS_TO_CBP,
        ),
    } as AppRecordKebabMenuItem,
  ],
  submitButtons: [
    {
      key: 'submitClaim',
      text: 'Submit claim',
      show: ({ existingRecord }) => canSubmitClaim(existingRecord),
      onConfirm: async ({ existingRecord, isValid }) => {
        if (!isValid) return { canceled: true };
        return validateBeforeSubmit(existingRecord?.id);
      },
      onSubmit: async ({
        updatedRecord,
        workOrderTaskType,
        workOrderTaskId,
        history,
      }) => {
        const isSubmittedToCbp = await submitClaimToCbp(updatedRecord);
        const exceptionTask = findExceptionTask(updatedRecord, {
          taskId: workOrderTaskId,
          taskType: workOrderTaskType,
        });
        if (!isSubmittedToCbp || !exceptionTask) return;
        await completeExceptionTaskAndOpenWorkOrder({
          updatedRecord,
          exceptionTaskId: exceptionTask.id,
          history,
        });
      },
    },
  ],
};

export default usDrawbackClaimRoute;
